import { type ExcalidrawElement } from '@excalidraw/excalidraw/types/element/types';
import { type BinaryFileData, type ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types/types';

import { checkPermission } from '../../../utils/permission';
import { UserRoles } from '../../roster/types';
import { type LibraryGroup, type Page, SessionTypes, type UserInfo } from '../types';

type ValueOf<T> = T[keyof T];

interface ImageType {
    readonly svg: 'image/svg+xml';
    readonly png: 'image/png';
    readonly jpg: 'image/jpeg';
    readonly gif: 'image/gif';
    readonly webp: 'image/webp';
    readonly bmp: 'image/bmp';
    readonly ico: 'image/x-icon';
}

const imageElementToFile = (element: ExcalidrawElement): BinaryFileData => {
    const extension = (element as any).key.split('.')[1] as string;
    const mimeType: ValueOf<ImageType> = `image/${extension}` as ValueOf<ImageType>;

    return {
        id: (element as any).fileId,
        dataURL: (element as any).url,
        mimeType,
        created: 0 // This [likely] doesn't matter.
    };
};

export const getFilesFromElements = (elements: ExcalidrawElement[]) =>
    elements.filter((e) => e.type === 'image' && (e as any).url).map(imageElementToFile);

/**
 * Given an array of elements, find elements with new a file ID and valid URL.
 * This is useful when elements are synced remotely and file definitions have not been updated in Excalidraw yet.
 * @param {ExcalidrawElement[]} elements
 * @param {ExcalidrawImperativeAPI} api
 */
export const getNewFilesFromElements = (elements: ExcalidrawElement[], api: ExcalidrawImperativeAPI) => {
    const existingFileIds = Object.keys(api?.getFiles());
    return elements
        .filter((e) => e.type === 'image' && (e as any).url && !existingFileIds.includes(e.fileId as string))
        .map(imageElementToFile);
};

/**
 * Process all libraries to load into whiteboard.
 */
export const getLibraries = (baseUrl, manifest): LibraryGroup[] => {
    try {
        const { libraryGroups } = manifest;
        libraryGroups.forEach((library: any) => {
            if (!library.files) return;
            // eslint-disable-next-line guard-for-in,no-restricted-syntax
            for (const fileId in library.files) {
                const file = library.files[fileId];
                if (file.dataURL.startsWith('/')) {
                    file.dataURL = `${baseUrl}/react/whiteboard${file.dataURL}`;
                }
            }
        });
        return libraryGroups;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed loading library manifest', e);
    }

    return [];
};

export const checkPagePermission = (page: Page, user: UserInfo, sessionType: SessionTypes) => {
    if (
        sessionType === SessionTypes.Annotations ||
        sessionType === SessionTypes.Shared ||
        sessionType === SessionTypes.Breakout ||
        sessionType === SessionTypes.Presentation
    ) {
        // Shared work spaces only need a role high enough to edit.
        return checkPermission(page.write_access, user.role);
    }

    // Check if user is page owner and editing is allowed.
    if (page.created_by === user.student_id && checkPermission(page.write_access, user.role)) {
        return true;
    }

    // Otherwise check if user is privileged enough. Assistant/teacher can edit individual whiteboards pages.
    return checkPermission(UserRoles.Assistant, user.role);
};

export const stringCompare = (object1, object2, propertyName) => {
    if (object1[propertyName] < object2[propertyName]) {
        return -1;
    }
    if (object1[propertyName] < object2[propertyName]) {
        return 1;
    }
    return 0;
};

/**
 * Log messages if a global variable `enableVerboseLogging` is set to true from the console.
 * Or REACT_APP_ENABLE_VERBOSE_LOGGING is set to true.
 * `console.log` calls slows down some browsers. Every millisecond wasted slows down the canvas experience.
 * @param message
 * @param optionalParams
 */
export const log = (message?: any, ...optionalParams: any[]) => {
    if (
        process.env.REACT_APP_ENABLE_VERBOSE_LOGGING ||
        (window as any).enableVerboseLogging ||
        (window as any).enableWebsocketLogging
    ) {
        // eslint-disable-next-line no-console
        console.log(message, optionalParams);
    }
};
